import React, { useState, useCallback, useContext, useMemo } from 'react';
import { 
    Avatar, 
    Box, 
    IconButton, 
    Menu, 
    MenuItem, 
    Tooltip, 
    Typography 
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../contexts/authContext';
import {useTheme} from '@mui/material/styles';



const ProfilMenuItem = ({ userData: { name = 'User', picurl } = {} }) => {
    const [anchorElUser, setAnchorElUser] = useState(null);
    const { logOut } = useContext(AuthContext);
    const navigate = useNavigate();
    const theme = useTheme();

    const handleOpenUserMenu = useCallback((event) => {
        setAnchorElUser(event.currentTarget);
    }, []);

    const handleCloseUserMenu = useCallback(() => {
        setAnchorElUser(null);
    }, []);

    const handleMyProjects = useCallback(() => {
        navigate("/myprojects");
        handleCloseUserMenu();
    }, [navigate, handleCloseUserMenu]);

    const handleUser = useCallback(() => {
        navigate("/user");
        handleCloseUserMenu();
    }, [navigate, handleCloseUserMenu]);

    const handleLogOut = useCallback(async () => {
        try {
            await logOut();
            navigate("/");
            handleCloseUserMenu();
        } catch (error) {
            console.error('Logout failed:', error);
            // Optionally, show a user-facing error message here
        }
    }, [logOut, navigate, handleCloseUserMenu]);

    const menuItems = useMemo(() => [
        { key: 'myprojects', label: 'My Projects', onClick: handleMyProjects },
        { key: 'profile', label: 'Profile', onClick: handleUser },
        { key: 'logout', label: 'Logout', onClick: handleLogOut }
    ], [handleMyProjects, handleUser, handleLogOut]);

    return (
        <Box sx={{ flexGrow: 0 }}>
            <Tooltip title={`${name} - Open settings`}>
                <IconButton 
                    onClick={handleOpenUserMenu} 
                    sx={{ 
                        p: 0, 
                        color: theme.palette.menuIcon.secondary,
                        '&:hover': {
                            backgroundColor: theme.palette.profileMenu.hover
                        }
                    }}
                    aria-label="User menu"
                    aria-controls={anchorElUser ? 'menu-appbar' : undefined}
                    aria-haspopup="true"
                >
                    <Avatar 
                        alt={name} 
                        src={picurl} 
                        sx={{ 
                            width: 40, 
                            height: 40,
                            border: `2px solid ${theme.palette.profileMenu.avatar.border}`,
                            '&:hover': {
                                border: `2px solid ${theme.palette.primary.main}`
                            }
                        }} 
                    />
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ 
                    mt: '45px',
                    '& .MuiPaper-root': {
                        backgroundColor: theme.palette.profileMenu.background,
                        border: `1px solid ${theme.palette.profileMenu.border}`,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '160px'
                    }
                }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                {menuItems.map(({ key, label, onClick }) => (
                    <MenuItem 
                        key={key} 
                        onClick={onClick}
                        sx={{
                            color: theme.palette.profileMenu.text,
                            '&:hover': {
                                backgroundColor: theme.palette.profileMenu.hover,
                                color: theme.palette.primary.main
                            },
                            '&:last-child': {
                                borderTop: `1px solid ${theme.palette.profileMenu.border}`,
                                marginTop: '4px',
                                paddingTop: '12px'
                            }
                        }}
                    >
                        <Typography 
                            textAlign="center" 
                            sx={{ 
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            {label}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export { ProfilMenuItem };